import { useState, useEffect, useCallback } from 'react';
import {
  getFirestore, collection, doc, getDoc, getDocs,
  onSnapshot, query, orderBy, limit, where,
} from 'firebase/firestore';
import { getApp } from 'firebase/app';
import { Link, useNavigate } from 'react-router-dom';
import { PORTAL_CARDS } from '../constants/portalCards';
import { getDashboardPortals, lookupPortalMeta } from '../utils/portalRegistry';
import DepartmentManager from './DepartmentManager';
import { ALL_THEMES } from '../theme/appTheme';
import { withAlpha } from '../design/themes';
import DailyTasksPage from './DailyTasksPage';
import ReportShareButton from '../components/ReportShare/ReportShareButton';
import { reportId, portalCode } from '../utils/reportId';
import { isV2 } from '../design/designFlags';
import '../design/v2.css';

const DB = () => getFirestore(getApp());
const MONO = "'Share Tech Mono', 'JetBrains Mono', monospace";
const HL   = '0.5px solid var(--smc-bd,rgba(255,255,255,0.07))';

// ── Helpers ───────────────────────────────────────────────────────────────────

export function todayIST() {
  return new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });
}
function monthPrefixIST() {
  return new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' }).slice(0, 7); // 'YYYY-MM'
}
export function fmtTime(ts) {
  if (!ts) return null;
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleTimeString('en-IN', { timeZone: 'Asia/Kolkata', hour: '2-digit', minute: '2-digit', hour12: false });
}
function fmtDuration(inTs, outTs) {
  if (!inTs || !outTs) return null;
  const ms = (outTs.toDate ? outTs.toDate() : new Date(outTs)) -
             (inTs.toDate  ? inTs.toDate()  : new Date(inTs));
  const h = Math.floor(ms / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  return `${h}h ${m}m`;
}
function fmtRelative(ts) {
  if (!ts) return '';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60)    return 'JUST NOW';
  if (diff < 3600)  return `${Math.floor(diff / 60)}M AGO`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}H AGO`;
  return `${Math.floor(diff / 86400)}D AGO`;
}
function fmtDate(ts) {
  if (!ts) return '—';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', year: 'numeric' });
}
function getGreeting() {
  const h = parseInt(new Date().toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', hour: 'numeric', hour12: false }));
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  return 'Good evening';
}
function getDateStr(now) {
  return now.toLocaleDateString('en-IN', {
    timeZone: 'Asia/Kolkata', weekday: 'long', day: '2-digit', month: 'long', year: 'numeric',
  }).toUpperCase().replace(/ /g, ' · ').replace(',', '');
}
function getInitials(name = '') {
  return name.split(' ').filter(Boolean).slice(0, 2).map(w => w[0]).join('').toUpperCase() || '??';
}
function workingDaysInMonth() {
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth();
  const today = now.getDate();
  let count = 0;
  for (let d = 1; d <= today; d++) {
    if (new Date(y, m, d).getDay() !== 0) count++; // exclude Sunday
  }
  return count;
}

// Portal styling is derived from portalCards.js + Firestore departments via portalRegistry.js

const AVATAR_COLORS = ['var(--smc-ac)', 'var(--smc-gn)', 'var(--smc-am)', 'var(--smc-pu)', '#fb923c', 'var(--smc-rd)'];

// ── Shared UI primitives ──────────────────────────────────────────────────────

function Badge({ children, color, bg, bd }) {
  return (
    <span style={{
      fontFamily: MONO, fontSize: 9.5, letterSpacing: '0.18em',
      padding: '3px 8px', borderRadius: 3, border: `0.5px solid ${bd || 'var(--smc-bd)'}`,
      background: bg || 'transparent', color, textTransform: 'uppercase', whiteSpace: 'nowrap',
    }}>{children}</span>
  );
}

function Avatar({ name, colorIdx = 0, size = 24 }) {
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%', flexShrink: 0,
      background: 'var(--smc-el,#111d36)', border: '0.5px solid rgba(255,255,255,0.10)',
      display: 'grid', placeItems: 'center',
      fontFamily: MONO, fontSize: size * 0.38,
      color: AVATAR_COLORS[colorIdx % AVATAR_COLORS.length],
    }}>
      {getInitials(name)}
    </div>
  );
}

function StatTile({ icon, label, value, unit, foot, deltaText, deltaUp, accentColor, onClick }) {
  return (
    <button
      type="button"
      className="smc-dashboard-stat-tile"
      onClick={onClick}
      style={{
        display: 'block', width: '100%', textAlign: 'left', cursor: 'pointer',
        background: 'transparent', border: 'none', padding: '14px 16px',
        borderRight: '0.5px solid var(--smc-bd,rgba(255,255,255,0.07))',
        borderBottom: '0.5px solid var(--smc-bd,rgba(255,255,255,0.07))',
        transition: 'background .12s',
        fontFamily: 'inherit', color: 'inherit',
      }}
      onMouseEnter={e => { e.currentTarget.style.background = 'var(--smc-hov,rgba(255,255,255,0.03))'; }}
      onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8, marginBottom: 10 }}>
        <div style={{
          width: 28, height: 28, borderRadius: 6, display: 'grid', placeItems: 'center',
          background: withAlpha(accentColor, '18'), border: `0.5px solid ${withAlpha(accentColor, '44')}`,
          color: accentColor, fontSize: 14, flexShrink: 0,
        }}>{icon}</div>
        {deltaText && (
          <span style={{
            fontFamily: MONO, fontSize: 9, letterSpacing: '0.06em', padding: '2px 7px',
            borderRadius: 999, border: '0.5px solid',
            color: deltaUp ? 'var(--smc-gn,#1D9E75)' : 'var(--smc-tx3)',
            borderColor: deltaUp ? 'rgba(29,158,117,0.3)' : 'var(--smc-bd)',
            background: deltaUp ? 'var(--smc-gn-bg)' : 'transparent',
            whiteSpace: 'nowrap',
          }}>{deltaText}</span>
        )}
      </div>
      <div style={{ fontFamily: MONO, fontSize: 9, letterSpacing: '0.2em', color: 'var(--smc-tx3)', marginBottom: 5 }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 700, letterSpacing: '-0.02em', color: 'var(--smc-tx)', lineHeight: 1 }}>
        {value}
        {unit && <span style={{ fontFamily: MONO, fontSize: 10, letterSpacing: '0.08em', color: 'var(--smc-tx3)', fontWeight: 400, marginLeft: 4 }}>{unit}</span>}
      </div>
      <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
        <span style={{ fontFamily: MONO, fontSize: 9, letterSpacing: '0.14em', color: 'var(--smc-tx3)' }}>{foot}</span>
        <span style={{ fontFamily: MONO, fontSize: 10, color: 'var(--smc-ac)', flexShrink: 0 }}>→</span>
      </div>
    </button>
  );
}

function UnifiedStatsCard({ title, meta, tiles }) {
  return (
    <CardShell title={title} meta={meta}>
      <div className="smc-dashboard-stats-unified">
        {tiles.map(tile => (
          <StatTile key={tile.label} {...tile} />
        ))}
      </div>
    </CardShell>
  );
}

function CardShell({ title, meta, children, rightEl }) {
  return (
    <div style={{ background: 'var(--smc-panel,#0d1526)', border: HL, borderRadius: 6, overflow: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px 12px', borderBottom: HL, gap: 10 }}>
        <span style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--smc-tx)', letterSpacing: '-0.005em', flex: 1 }}>{title}</span>
        {meta && <span style={{ fontFamily: MONO, fontSize: 9.5, letterSpacing: '0.18em', color: 'var(--smc-tx3)', whiteSpace: 'nowrap' }}>{meta}</span>}
        {rightEl}
      </div>
      <div>{children}</div>
    </div>
  );
}

// ── India National Holidays ───────────────────────────────────────────────────
const INDIA_HOLIDAYS = {
  '2026-01-26': 'Republic Day',
  '2026-03-25': 'Holi',
  '2026-04-02': 'Good Friday',
  '2026-04-14': 'Ambedkar Jayanti',
  '2026-05-01': 'Labour Day',
  '2026-05-12': 'Buddha Purnima',
  '2026-08-15': 'Independence Day',
  '2026-10-02': 'Gandhi Jayanti',
  '2026-10-20': 'Dussehra',
  '2026-11-09': 'Diwali',
  '2026-11-27': 'Guru Nanak Jayanti',
  '2026-12-25': 'Christmas',
};

function getMonthCalendarData() {
  const now  = new Date();
  const y    = now.getFullYear();
  const m    = now.getMonth();
  const daysInMonth = new Date(y, m + 1, 0).getDate();
  const todayStr    = now.toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });
  // week starts Monday: dow 1→0, 2→1…0(Sun)→6
  const firstDow    = new Date(y, m, 1).getDay();
  const colOffset   = firstDow === 0 ? 6 : firstDow - 1;

  const days = [];
  for (let d = 1; d <= daysInMonth; d++) {
    const date        = `${y}-${String(m + 1).padStart(2,'0')}-${String(d).padStart(2,'0')}`;
    const rawDow      = new Date(y, m, d).getDay();
    const isSunday    = rawDow === 0;
    const holidayName = INDIA_HOLIDAYS[date];
    days.push({ date, day: d, isSunday, isHoliday: !!holidayName, holidayName, isFuture: date > todayStr, isToday: date === todayStr });
  }
  return { days, colOffset };
}

// ── Member Detail Modal ───────────────────────────────────────────────────────

export function MemberDetailModal({ member, todayAtt, colorIdx, customDepts = [], onClose }) {
  const [monthRecords, setMonthRecords] = useState([]);
  const [memberReports, setMemberReports] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!member?.uid) return;
    const prefix = monthPrefixIST();

    getDocs(collection(DB(), 'attendance', member.uid, 'records'))
      .then(snap => {
        const records = snap.docs
          .map(d => ({ date: d.id, ...d.data() }))
          .filter(r => r.date?.startsWith(prefix))
          .sort((a, b) => b.date.localeCompare(a.date));
        setMonthRecords(records);
      })
      .catch(() => {});

    const feedPortals = PORTAL_CARDS.filter(p => p.collection);
    let all = [];
    let done = 0;
    feedPortals.forEach(p => {
      const uidField  = p.authorUidField  || 'uploadedByUid';
      const dateField = p.dateField       || 'uploadedAt';
      getDocs(
        query(
          collection(DB(), p.collection),
          where(uidField, '==', member.uid),
          orderBy(dateField, 'desc'),
          limit(5)
        )
      )
        .then(snap => {
          const posts = snap.docs
            .map(d => ({ id: d.id, portalId: p.id, _dateField: dateField, ...d.data() }))
            .filter(d => !d.parentId);
          all = [...all, ...posts];
        })
        .catch(() => {})
        .finally(() => {
          done++;
          if (done === feedPortals.length) {
            all.sort((a, b) => {
              const ta = (a[a._dateField]?.toMillis ? a[a._dateField].toMillis() : 0);
              const tb = (b[b._dateField]?.toMillis ? b[b._dateField].toMillis() : 0);
              return tb - ta;
            });
            setMemberReports(all.slice(0, 8));
            setLoading(false);
          }
        });
    });
    if (!feedPortals.length) setLoading(false);
  }, [member?.uid]);

  const checkedIn   = todayAtt?.isOpen === true;
  const checkedOut  = todayAtt?.checkedInAt && todayAtt?.isOpen === false;
  const workDays    = workingDaysInMonth();
  const presentSet  = new Set(monthRecords.filter(r => r.checkedInAt).map(r => r.date));
  const daysPresent = presentSet.size;
  const attRate     = workDays > 0 ? Math.round((daysPresent / workDays) * 100) : 0;
  const { days, colOffset } = getMonthCalendarData();

  // Holidays in current month
  const monthHolidays = days.filter(d => d.isHoliday);

  return (
    <div
      onClick={e => e.target === e.currentTarget && onClose()}
      style={{
        position: 'fixed', inset: 0, zIndex: 9000,
        background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(4px)',
        display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-end',
      }}
    >
      <div style={{
        width: 440, maxWidth: '100vw', height: '100vh', overflowY: 'auto',
        background: 'var(--smc-panel,#0d1526)', borderLeft: HL,
        animation: 'db-slide-in 0.18s ease',
      }}>
        <style>{`
          @keyframes db-slide-in { from { transform: translateX(40px); opacity:0 } to { transform: none; opacity:1 } }
          .mdt-day:hover { opacity: 0.85 !important; }
        `}</style>

        {/* Header */}
        <div style={{ padding: '20px 20px 16px', borderBottom: HL, display: 'flex', alignItems: 'flex-start', gap: 14 }}>
          <Avatar name={member.name || member.email} colorIdx={colorIdx} size={44} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--smc-tx)' }}>{member.name || member.email?.split('@')[0]}</div>
            <div style={{ fontFamily: MONO, fontSize: 9, letterSpacing: '0.18em', color: 'var(--smc-tx3)', marginTop: 3 }}>
              {(member.role || 'MEMBER').toUpperCase()}
            </div>
            {member.email && (
              <div style={{ fontSize: 10.5, color: 'var(--smc-tx3)', marginTop: 3 }}>{member.email}</div>
            )}
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'transparent', border: '0.5px solid var(--smc-bd)', color: 'var(--smc-tx3)',
              borderRadius: 4, width: 26, height: 26, cursor: 'pointer', fontSize: 14, flexShrink: 0,
            }}
          >✕</button>
        </div>

        {/* Today's attendance */}
        <div style={{ padding: '16px 20px', borderBottom: HL }}>
          <div style={{ fontFamily: MONO, fontSize: 9, letterSpacing: '0.22em', color: 'var(--smc-tx3)', marginBottom: 12 }}>TODAY'S ATTENDANCE</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
            {[
              ['CHECK IN',  fmtTime(todayAtt?.checkedInAt)  || '—'],
              ['CHECK OUT', fmtTime(todayAtt?.checkoutTime) || '—'],
              ['DURATION',  fmtDuration(todayAtt?.checkedInAt, todayAtt?.checkoutTime) || (checkedIn ? 'LIVE' : '—')],
            ].map(([k, v]) => (
              <div key={k} style={{ background: 'var(--smc-el,#111d36)', border: HL, borderRadius: 5, padding: '10px 12px' }}>
                <div style={{ fontFamily: MONO, fontSize: 8, letterSpacing: '0.2em', color: 'var(--smc-tx3)', marginBottom: 6 }}>{k}</div>
                <div style={{
                  fontFamily: MONO, fontSize: 14, fontWeight: 700, lineHeight: 1,
                  color: (k === 'CHECK IN' && checkedIn) ? 'var(--smc-gn,#1D9E75)'
                       : (k === 'DURATION' && checkedIn) ? 'var(--smc-am,#BA7517)'
                       : 'var(--smc-tx)',
                }}>{v}</div>
              </div>
            ))}
          </div>
          <div style={{ marginTop: 9, display: 'flex', gap: 7, flexWrap: 'wrap' }}>
            {checkedIn  && <Badge color="var(--smc-gn)" bg="var(--smc-gn-bg,rgba(29,158,117,0.10))" bd="rgba(29,158,117,0.28)">● ACTIVE NOW</Badge>}
            {checkedOut && <Badge color="var(--smc-tx3)" bg="var(--smc-el)" bd="var(--smc-bd)">CHECKED OUT</Badge>}
            {!todayAtt  && <Badge color="var(--smc-tx3)" bg="transparent" bd="var(--smc-bd)">NOT CHECKED IN</Badge>}
            {todayAtt?.workType && <Badge color="var(--smc-tx2)" bg="var(--smc-el)" bd="var(--smc-bd)">{todayAtt.workType.toUpperCase()}</Badge>}
          </div>
        </div>

        {/* Monthly stats */}
        <div style={{ padding: '16px 20px', borderBottom: HL }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
            <div style={{ fontFamily: MONO, fontSize: 9, letterSpacing: '0.22em', color: 'var(--smc-tx3)' }}>
              THIS MONTH · {new Date().toLocaleDateString('en-IN', { month: 'long', year: 'numeric', timeZone: 'Asia/Kolkata' }).toUpperCase()}
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 8, marginBottom: 16 }}>
            {[
              ['DAYS PRESENT', daysPresent, null],
              ['WORKING DAYS', workDays,    null],
              ['ATTENDANCE',   `${attRate}%`, attRate >= 80 ? 'var(--smc-gn)' : attRate >= 60 ? 'var(--smc-am)' : 'var(--smc-rd)'],
            ].map(([k, v, col]) => (
              <div key={k} style={{ background: 'var(--smc-el)', border: HL, borderRadius: 5, padding: '10px 12px' }}>
                <div style={{ fontFamily: MONO, fontSize: 8, letterSpacing: '0.2em', color: 'var(--smc-tx3)', marginBottom: 6 }}>{k}</div>
                <div style={{ fontFamily: MONO, fontSize: 20, fontWeight: 700, lineHeight: 1, color: col || 'var(--smc-tx)' }}>{v}</div>
              </div>
            ))}
          </div>

          {/* Monthly calendar grid */}
          <div>
            {/* Day headers Mon→Sun */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: 2, marginBottom: 4 }}>
              {['M','T','W','T','F','S','S'].map((d, i) => (
                <div key={i} style={{
                  fontFamily: MONO, fontSize: 8, textAlign: 'center', letterSpacing: '0.08em',
                  color: i === 6 ? 'var(--smc-rd,#E24B4A)' : 'var(--smc-tx3)',
                  paddingBottom: 2,
                }}>{d}</div>
              ))}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: 2 }}>
              {/* Empty cells before first day */}
              {Array.from({ length: colOffset }).map((_, i) => (
                <div key={`e${i}`} />
              ))}
              {days.map(({ date, day, isSunday, isHoliday, holidayName, isFuture, isToday }) => {
                const isPresent = presentSet.has(date);
                const isAbsent  = !isFuture && !isSunday && !isHoliday && !isPresent;
                const bg = isFuture    ? 'transparent'
                         : isHoliday  ? 'rgba(245,158,11,0.15)'
                         : isSunday   ? 'rgba(255,255,255,0.02)'
                         : isPresent  ? 'rgba(29,158,117,0.20)'
                         : isAbsent   ? 'rgba(226,75,74,0.12)'
                         : 'transparent';
                const dotColor = isHoliday ? 'var(--smc-am,#BA7517)'
                               : isPresent ? 'var(--smc-gn,#1D9E75)'
                               : isAbsent  ? 'var(--smc-rd,#E24B4A)'
                               : isSunday  ? 'var(--smc-tx3)'
                               : 'var(--smc-el)';
                return (
                  <div
                    key={date}
                    className="mdt-day"
                    title={isHoliday ? `${date} — ${holidayName}` : date}
                    style={{
                      background: bg,
                      border: isToday ? '0.5px solid var(--smc-ac,#0e9cc0)' : HL,
                      borderRadius: 3,
                      padding: '4px 2px',
                      display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2,
                      opacity: isFuture ? 0.3 : 1,
                      cursor: 'default',
                    }}
                  >
                    <div style={{
                      fontFamily: MONO, fontSize: 8, lineHeight: 1,
                      color: isToday ? 'var(--smc-ac)' : isSunday ? 'var(--smc-rd)' : 'var(--smc-tx3)',
                      fontWeight: isToday ? 700 : 400,
                    }}>{day}</div>
                    <div style={{ width: 4, height: 4, borderRadius: '50%', background: dotColor, flexShrink: 0 }} />
                  </div>
                );
              })}
            </div>

            {/* Legend */}
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 10 }}>
              {[
                ['var(--smc-gn)', 'Present'],
                ['var(--smc-rd)', 'Absent'],
                ['var(--smc-am)', 'Holiday'],
                ['var(--smc-tx3)', 'Sunday'],
              ].map(([color, label]) => (
                <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <div style={{ width: 6, height: 6, borderRadius: '50%', background: color }} />
                  <span style={{ fontFamily: MONO, fontSize: 8, letterSpacing: '0.1em', color: 'var(--smc-tx3)' }}>{label.toUpperCase()}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Holidays this month */}
        {monthHolidays.length > 0 && (
          <div style={{ padding: '14px 20px', borderBottom: HL }}>
            <div style={{ fontFamily: MONO, fontSize: 9, letterSpacing: '0.22em', color: 'var(--smc-tx3)', marginBottom: 10 }}>
              HOLIDAYS THIS MONTH · {monthHolidays.length}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {monthHolidays.map(h => (
                <div key={h.date} style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  background: 'rgba(245,158,11,0.07)', border: '0.5px solid rgba(245,158,11,0.20)',
                  borderRadius: 4, padding: '8px 12px',
                }}>
                  <div style={{ width: 3, height: 28, borderRadius: 2, background: 'var(--smc-am,#BA7517)', flexShrink: 0 }} />
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--smc-tx)' }}>{h.holidayName}</div>
                    <div style={{ fontFamily: MONO, fontSize: 8.5, letterSpacing: '0.12em', color: 'var(--smc-tx3)', marginTop: 2 }}>
                      {new Date(h.date + 'T00:00:00').toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' }).toUpperCase()}
                    </div>
                  </div>
                  <span style={{ marginLeft: 'auto', fontFamily: MONO, fontSize: 8, color: 'var(--smc-am)', letterSpacing: '0.1em' }}>HOLIDAY</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Recent reports */}
        <div style={{ padding: '16px 20px' }}>
          <div style={{ fontFamily: MONO, fontSize: 9, letterSpacing: '0.22em', color: 'var(--smc-tx3)', marginBottom: 12 }}>
            RECENT REPORTS · {loading ? '…' : memberReports.length}
          </div>
          {loading ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {[1,2,3].map(i => (
                <div key={i} style={{ height: 44, background: 'var(--smc-el)', border: HL, borderRadius: 5,
                  animation: 'pt-shimmer 1.5s ease-in-out infinite', opacity: 0.6 }} />
              ))}
            </div>
          ) : memberReports.length === 0 ? (
            <div style={{ fontFamily: MONO, fontSize: 10, color: 'var(--smc-tx3)', padding: '8px 0' }}>No reports submitted yet.</div>
          ) : memberReports.map((r, i) => {
            const meta   = lookupPortalMeta(r.portalId, customDepts) || {};
            const portal = PORTAL_CARDS.find(p => p.id === r.portalId);
            const deptPath = customDepts.find(d => d.id === r.portalId);
            const dateTs = r[r._dateField];
            return (
              <Link
                key={r.id}
                to={portal?.path || (deptPath ? `/dept/${deptPath.id}` : '/')}
                style={{
                  display: 'flex', alignItems: 'flex-start', gap: 10,
                  padding: '10px 0', borderBottom: i < memberReports.length - 1 ? HL : 'none',
                  textDecoration: 'none',
                }}
              >
                <div style={{ width: 3, alignSelf: 'stretch', borderRadius: 2, background: meta.color || 'var(--smc-ac)', flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--smc-tx)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {r.title || r.description?.slice(0, 60) || 'Untitled'}
                  </div>
                  <div style={{ fontFamily: MONO, fontSize: 8.5, letterSpacing: '0.12em', color: 'var(--smc-tx3)', marginTop: 3 }}>
                    {fmtDate(dateTs)} · {(meta.label || '').toUpperCase()}
                    {r.adminApproved && <span style={{ color: 'var(--smc-gn)', marginLeft: 6 }}>✓ APPROVED</span>}
                  </div>
                </div>
                <span style={{ fontFamily: MONO, fontSize: 9, color: 'var(--smc-ac)', whiteSpace: 'nowrap', marginTop: 2, flexShrink: 0 }}>→</span>
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ── New Portal Activity dropdown ──────────────────────────────────────────────

function NewPortalDropdown({ customDepts, onCreateDept, onClose }) {
  const dashboardPortals = getDashboardPortals(customDepts);
  return (
    <div style={{
      position: 'absolute', top: '100%', right: 0, marginTop: 6, zIndex: 500,
      background: 'var(--smc-panel,#0d1526)', border: HL, borderRadius: 6,
      minWidth: 240, boxShadow: 'var(--smc-shadow,0 4px 20px rgba(0,0,0,0.35))',
    }}>
      <button
        type="button"
        onClick={() => { onCreateDept?.(); onClose?.(); }}
        style={{
          display: 'flex', alignItems: 'center', gap: 10, width: '100%',
          padding: '11px 14px', background: 'var(--smc-ac-bg,rgba(14,156,192,0.10))',
          border: 'none', borderBottom: HL, cursor: 'pointer', textAlign: 'left',
        }}
      >
        <span style={{ fontSize: 16 }}>＋</span>
        <span style={{ fontSize: 12.5, color: 'var(--smc-ac,#0e9cc0)', fontWeight: 600 }}>
          Create new department…
        </span>
      </button>
      <div style={{ padding: '10px 14px 8px', fontFamily: MONO, fontSize: 9, letterSpacing: '0.22em', color: 'var(--smc-tx3)', borderBottom: HL }}>
        POST TO PORTAL
      </div>
      {dashboardPortals.map(({ meta }) => (
        <Link
          key={meta.id}
          to={meta.path}
          onClick={onClose}
          style={{
            display: 'flex', alignItems: 'center', gap: 10,
            padding: '9px 14px', textDecoration: 'none',
            borderBottom: HL, transition: 'background .1s',
          }}
          onMouseEnter={e => e.currentTarget.style.background = 'var(--smc-hov,rgba(255,255,255,0.03))'}
          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
        >
          <div style={{ width: 8, height: 8, borderRadius: '50%', background: meta.color, flexShrink: 0 }} />
          <span style={{ fontSize: 12.5, color: 'var(--smc-tx)', fontWeight: 500 }}>{meta.label}</span>
          <span style={{ fontFamily: MONO, fontSize: 9, color: 'var(--smc-tx3)', marginLeft: 'auto' }}>→</span>
        </Link>
      ))}
    </div>
  );
}

// ── Compact theme picker ──────────────────────────────────────────────────────
function ThemePicker({ themeId, onThemeChange }) {
  const [open, setOpen] = useState(false);
  const active = ALL_THEMES.find(t => t.id === themeId) || ALL_THEMES[0];
  return (
    <div style={{ position: 'relative' }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          display: 'flex', alignItems: 'center', gap: 6,
          padding: '6px 10px', borderRadius: 5,
          border: HL, background: 'transparent',
          color: 'var(--smc-tx3)', fontFamily: MONO, fontSize: 10,
          letterSpacing: '0.1em', cursor: 'pointer',
          transition: 'all 0.15s',
        }}
        onMouseEnter={e => { e.currentTarget.style.background = 'var(--smc-ac-bg,rgba(14,156,192,0.10))'; e.currentTarget.style.color = 'var(--smc-ac,#0e9cc0)'; }}
        onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--smc-tx3)'; }}
      >
        <span style={{ width: 12, height: 12, borderRadius: 3, background: active.accent || '#0e9cc0', display: 'inline-block', flexShrink: 0 }} />
        {active.name}
        <span style={{ fontSize: 7 }}>{open ? '▲' : '▼'}</span>
      </button>
      {open && (
        <>
          <div onClick={() => setOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 98 }} />
          <div style={{
            position: 'absolute', top: 'calc(100% + 6px)', right: 0, zIndex: 99,
            minWidth: 200, background: 'var(--smc-panel,#0d1526)',
            border: HL, borderRadius: 8, overflow: 'hidden',
            boxShadow: '0 8px 32px rgba(0,0,0,0.40)',
          }}>
            <div style={{ padding: '8px 12px 6px', fontFamily: MONO, fontSize: 9,
              letterSpacing: '0.18em', color: 'var(--smc-tx3)', borderBottom: HL }}>APPEARANCE</div>
            {ALL_THEMES.map(t => (
              <div key={t.id} onClick={() => { onThemeChange?.(t.id); setOpen(false); }}
                style={{
                  display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px',
                  cursor: 'pointer',
                  background: t.id === themeId ? 'var(--smc-ac-bg,rgba(14,156,192,0.10))' : 'transparent',
                  transition: 'background 0.1s',
                }}
                onMouseEnter={e => { if (t.id !== themeId) e.currentTarget.style.background = 'var(--smc-hov,rgba(255,255,255,0.04))'; }}
                onMouseLeave={e => { if (t.id !== themeId) e.currentTarget.style.background = 'transparent'; }}
              >
                <div style={{ width: 28, height: 18, borderRadius: 4, overflow: 'hidden',
                  border: HL, flexShrink: 0, display: 'flex' }}>
                  <div style={{ flex: 1, background: t.bg || '#0a0f1e' }} />
                  <div style={{ flex: 1, background: t.panel || '#0d1526' }} />
                  <div style={{ flex: 1, background: t.accent || '#0e9cc0' }} />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 12, fontWeight: 500,
                    color: t.id === themeId ? 'var(--smc-ac,#0e9cc0)' : 'var(--smc-tx)' }}>{t.name}</div>
                  <div style={{ fontFamily: MONO, fontSize: 8.5, color: 'var(--smc-tx3)', marginTop: 1 }}>
                    {t.dark ? 'Dark' : 'Light'}
                  </div>
                </div>
                {t.id === themeId && <span style={{ fontFamily: MONO, fontSize: 9, color: 'var(--smc-ac)' }}>✓</span>}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function DashboardHome({ role, userName, userId, themeId, onThemeChange, onTabChange }) {
  const navigate = useNavigate();
  const [clock,             setClock]             = useState(new Date());
  const [team,              setTeam]              = useState([]);
  const [attendance,        setAttendance]        = useState({});
  const [recentReports,     setRecentReports]     = useState([]);
  const [portalCounts,      setPortalCounts]      = useState({});
  const [reportChip,        setReportChip]        = useState('ALL');
  const [selectedMember,    setSelectedMember]    = useState(null);
  const [showPortalDrop,    setShowPortalDrop]    = useState(false);
  const [sharedReports,     setSharedReports]     = useState([]);
  const [sharedPortalModal, setSharedPortalModal] = useState(null); // portal id
  const [customDepts,       setCustomDepts]       = useState([]);
  const [showDeptManager,   setShowDeptManager]   = useState(false);
  const [deptManagerMode,   setDeptManagerMode]   = useState(null); // 'new' | null

  // Role guards
  const isAdminView = role === 'admin' || role === 'ops';
  const myPortalId  = PORTAL_CARDS.find(p => p.id === role)?.id
    || customDepts.find(d => d.id === role)?.id
    || null;

  // Live clock
  useEffect(() => {
    const t = setInterval(() => setClock(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  // Team members — live
  useEffect(() => {
    try {
      const unsub = onSnapshot(collection(DB(), 'authorized_users'), snap => {
        const members = snap.docs
          .map(d => ({ uid: d.id, ...d.data() }))
          // Deactivated members (active:false) drop out of attendance + counts
          .filter(m => m.active !== false && m.status !== 'revoked' && m.role !== 'mentor' && m.role !== 'finance');
        setTeam(members);
      });
      return unsub;
    } catch { return undefined; }
  }, []);

  // Today's attendance — real-time per-member listeners
  useEffect(() => {
    if (!team.length) return;
    const today = todayIST();
    const unsubs = team.map(m =>
      onSnapshot(
        doc(DB(), 'attendance', m.uid, 'records', today),
        snap => setAttendance(prev => ({ ...prev, [m.uid]: snap.exists() ? snap.data() : null })),
        () => setAttendance(prev => ({ ...prev, [m.uid]: null }))
      )
    );
    return () => unsubs.forEach(u => u());
  }, [team]); // eslint-disable-line

  // Admin-created departments (no code deploy needed)
  useEffect(() => {
    try {
      return onSnapshot(collection(DB(), 'departments'), snap => {
        setCustomDepts(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      });
    } catch { return undefined; }
  }, []);

  // Recent reports — field is uploadedAt, NOT ts
  const loadReports = useCallback(() => {
    const dashboardPortals = getDashboardPortals(customDepts);
    let all = [];
    let done = 0;
    const total = dashboardPortals.length || 1;

    dashboardPortals.forEach(({ card, meta }) => {
      const col = card.collection;
      const portalId = meta.id;
      const isCustom = !meta.builtIn;

      const q = isCustom
        ? query(collection(DB(), col), where('deptId', '==', portalId), orderBy('uploadedAt', 'desc'), limit(6))
        : query(collection(DB(), col), orderBy('uploadedAt', 'desc'), limit(6));

      getDocs(q)
        .then(snap => {
          const posts = snap.docs
            .map(d => ({ id: d.id, portalId, ...d.data() }))
            .filter(d => !d.parentId);
          all = [...all, ...posts];
        })
        .catch(() => {})
        .finally(() => {
          done++;
          if (done === total) {
            all.sort((a, b) => {
              const ta = a.uploadedAt?.toMillis ? a.uploadedAt.toMillis() : 0;
              const tb = b.uploadedAt?.toMillis ? b.uploadedAt.toMillis() : 0;
              return tb - ta;
            });
            setRecentReports(all.slice(0, 8));
          }
        });

      // Portal counts
      const countQ = isCustom
        ? query(collection(DB(), col), where('deptId', '==', portalId))
        : collection(DB(), col);
      getDocs(countQ)
        .then(snap => {
          const topLevel = snap.docs.filter(d => !d.data().parentId).length;
          setPortalCounts(prev => ({ ...prev, [portalId]: topLevel }));
        })
        .catch(() => {});
    });

    if (dashboardPortals.length === 0) setRecentReports([]);
  }, [customDepts]);

  useEffect(() => { loadReports(); }, [loadReports]);

  // Load cross-dept reports explicitly shared with this user
  useEffect(() => {
    if (!userId || isAdminView) return;
    const feedPortals = PORTAL_CARDS.filter(p => p.collection && p.id !== myPortalId);
    if (!feedPortals.length) return;
    let all = [];
    let done = 0;
    feedPortals.forEach(p => {
      getDocs(query(collection(DB(), p.collection), where('allowedUsers', 'array-contains', userId)))
        .then(snap => {
          all = [...all, ...snap.docs.map(d => ({ id: d.id, portalId: p.id, ...d.data() })).filter(d => !d.parentId)];
        })
        .catch(() => {})
        .finally(() => {
          done++;
          if (done === feedPortals.length) {
            all.sort((a, b) => (b.uploadedAt?.toMillis?.() || 0) - (a.uploadedAt?.toMillis?.() || 0));
            setSharedReports(all);
          }
        });
    });
  }, [userId, isAdminView, myPortalId]); // eslint-disable-line

  // Close portal dropdown on outside click
  useEffect(() => {
    if (!showPortalDrop) return;
    // Ignore clicks inside the + ADD wrapper — closing (and unmounting) the
    // dropdown in the capture phase would swallow the menu item's own onClick.
    const handler = e => {
      if (e.target.closest?.('[data-portal-drop]')) return;
      setShowPortalDrop(false);
    };
    document.addEventListener('click', handler, true);
    return () => document.removeEventListener('click', handler, true);
  }, [showPortalDrop]);

  // ── Derived ───────────────────────────────────────────────────────────────
  const checkedInCount = team.filter(m => attendance[m.uid]?.isOpen === true).length;
  const myAttToday   = attendance[userId];
  const checkedInNow = myAttToday?.isOpen === true;
  const totalReports = Object.values(portalCounts).reduce((a, b) => a + b, 0);
  const activePortals = getDashboardPortals(customDepts);

  // Role-filtered portals and reports
  const visiblePortals = isAdminView
    ? activePortals
    : activePortals.filter(({ meta }) => meta.id === myPortalId);

  // Portals with shared reports (not the user's own portal)
  const sharedPortalIds = [...new Set(sharedReports.map(r => r.portalId))];
  const sharedPortalCards = sharedPortalIds
    .map(id => PORTAL_CARDS.find(p => p.id === id))
    .filter(Boolean);

  const timeStr = clock.toLocaleTimeString('en-IN', {
    timeZone: 'Asia/Kolkata', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
  });

  // Role-aware report list
  const roleFilteredReports = isAdminView
    ? recentReports
    : recentReports.filter(r =>
        r.portalId === myPortalId || sharedReports.some(s => s.id === r.id)
      );

  const filteredReports = roleFilteredReports.filter(r => {
    if (reportChip === 'MINE')     return r.uploadedByUid === userId || r.uploadedBy === userName;
    if (reportChip === 'APPROVED') return r.approved === true;
    return true;
  });

  const goTab = (tab, opts) => onTabChange?.(tab, opts);

  const statTiles = isAdminView ? [
    {
      icon: '🚀', label: 'Mission Phase', value: 'Phase 1', foot: 'IAD · ELECTRIC THRUSTER',
      deltaText: 'ON TRACK', deltaUp: true, accentColor: 'var(--smc-ac,#0e9cc0)',
      onClick: () => goTab('missions'),
    },
    {
      icon: '👥', label: 'Team Members', value: team.length || '—', unit: team.length > 0 ? 'active' : '',
      foot: `${checkedInCount} ON DUTY TODAY`,
      deltaText: team.length > 0 ? `${checkedInCount} IN` : null, deltaUp: checkedInCount > 0,
      accentColor: 'var(--smc-gn,#1D9E75)',
      onClick: () => goTab('team', { teamSubTab: 'attendance' }),
    },
    {
      icon: '📄', label: 'Total Reports', value: totalReports || '—', foot: 'ACROSS ALL PORTALS',
      deltaText: null, accentColor: 'var(--smc-am,#BA7517)',
      onClick: () => goTab('daily-log'),
    },
    {
      icon: '✓', label: 'Portals Active', value: activePortals.length,
      foot: activePortals.map(p => (p.meta?.id || p.card?.id || '').toUpperCase().slice(0, 3)).filter(Boolean).join(' · '),
      deltaText: `${activePortals.length} LIVE`, deltaUp: true, accentColor: 'var(--smc-pu,#7F77DD)',
      onClick: () => goTab('dashboard'),
    },
  ] : [
    {
      icon: '🚀', label: 'Mission Phase', value: 'Phase 1', foot: 'IAD · ELECTRIC THRUSTER',
      deltaText: 'ON TRACK', deltaUp: true, accentColor: 'var(--smc-ac,#0e9cc0)',
      onClick: () => goTab('missions'),
    },
    {
      icon: '📅', label: 'My Attendance',
      value: checkedInNow ? 'IN' : myAttToday ? 'OUT' : '—',
      foot: checkedInNow ? `SINCE ${fmtTime(myAttToday?.checkedInAt) || '—'}` : myAttToday ? 'CHECKED OUT' : 'NOT CHECKED IN',
      deltaText: checkedInNow ? 'ACTIVE' : null, deltaUp: checkedInNow,
      accentColor: 'var(--smc-gn,#1D9E75)',
      onClick: () => goTab('attendance'),
    },
    {
      icon: '📄', label: 'My Reports', value: roleFilteredReports.length || '—',
      foot: myPortalId ? (lookupPortalMeta(myPortalId, customDepts)?.label || myPortalId).toUpperCase() : 'SHARED WITH ME',
      deltaText: sharedReports.length > 0 ? `${sharedReports.length} SHARED` : null, deltaUp: true,
      accentColor: 'var(--smc-am,#BA7517)',
      onClick: () => goTab('daily-log'),
    },
    {
      icon: '🏢', label: 'My Department',
      value: myPortalId ? (lookupPortalMeta(myPortalId, customDepts)?.label?.split(' ')[0] || '—') : 'NONE',
      foot: myPortalId ? 'OPEN DEPARTMENT PORTAL' : 'NO DEPT ASSIGNED',
      deltaText: sharedPortalCards.length > 0 ? `${sharedPortalCards.length} SHARED` : null, deltaUp: true,
      accentColor: 'var(--smc-pu,#7F77DD)',
      onClick: () => {
        const portal = PORTAL_CARDS.find(p => p.id === myPortalId);
        const path = portal?.path || lookupPortalMeta(myPortalId, customDepts)?.path;
        if (path) navigate(path);
      },
    },
  ];

  // ── Render: redesigned admin home (v2) ───────────────────────────────────
  if (isAdminView && isV2('home')) {
    return (
      <>
        <AdminHomeV2
          userId={userId} userName={userName} clock={clock}
          team={team} attendance={attendance} customDepts={customDepts}
          portalCounts={portalCounts} totalReports={totalReports}
          activePortals={activePortals} recentReports={recentReports}
          goTab={goTab}
          onOpenMember={(member, colorIdx) => setSelectedMember({ member, colorIdx })}
          addPortalControl={
            <div data-portal-drop style={{ position: 'relative' }}>
              <button type="button" className="smc2-btn" style={{ height: 26 }}
                onClick={e => { e.stopPropagation(); setShowPortalDrop(v => !v); }}>+ ADD</button>
              {showPortalDrop && (
                <NewPortalDropdown
                  customDepts={customDepts}
                  onCreateDept={() => { setDeptManagerMode('new'); setShowDeptManager(true); }}
                  onClose={() => setShowPortalDrop(false)}
                />
              )}
            </div>
          }
        />
        {selectedMember && (
          <MemberDetailModal
            member={selectedMember.member}
            todayAtt={attendance[selectedMember.member.uid]}
            colorIdx={selectedMember.colorIdx}
            customDepts={customDepts}
            onClose={() => setSelectedMember(null)}
          />
        )}
        {showDeptManager && (
          <DepartmentManager
            isAdmin userId={userId} userName={userName} initialEditing={deptManagerMode}
            onClose={() => { setShowDeptManager(false); setDeptManagerMode(null); }}
          />
        )}
      </>
    );
  }

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="smc-dashboard" style={{ background: 'var(--smc-bg,#0a0f1e)', minHeight: '100%', fontFamily: "'Inter', -apple-system, sans-serif", fontSize: 13 }}>
      <style>{`
        @keyframes db-pulse { 0%,100%{opacity:1} 50%{opacity:0.5} }
        @keyframes db-slide-in { from { transform: translateX(40px); opacity:0 } to { transform: none; opacity:1 } }
        .db-chip:hover { color: var(--smc-tx2) !important; border-color: rgba(255,255,255,0.14) !important; }
        .db-read-link:hover { color: var(--smc-tx) !important; }
        .db-btn:hover { background: rgba(14,156,192,0.18) !important; border-color: var(--smc-ac) !important; }
      `}</style>

      {/* ── Top bar ─────────────────────────────────────────────────────── */}
      <div className="smc-dashboard-topbar" style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '18px 28px', borderBottom: HL }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 18, fontWeight: 700, letterSpacing: '-0.01em', color: 'var(--smc-tx)' }}>
            {getGreeting()}, {userName?.split(' ')[0] || 'Commander'} 👋
          </h1>
          <div style={{ marginTop: 3, fontFamily: MONO, fontSize: 10.5, letterSpacing: '0.18em', color: 'var(--smc-tx3)' }}>
            {getDateStr(clock)} · IST
          </div>
        </div>
        <div className="smc-dashboard-topbar-actions" style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 10 }}>
          {/* Status pill */}
          {checkedInNow ? (
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: 8, padding: '6px 11px 6px 9px',
              border: '0.5px solid rgba(29,158,117,0.3)', background: 'var(--smc-gn-bg,rgba(29,158,117,0.10))',
              color: 'var(--smc-gn,#1D9E75)', borderRadius: 999,
              fontFamily: MONO, fontSize: 11, letterSpacing: '0.08em',
            }}>
              <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--smc-gn)', boxShadow: '0 0 0 3px rgba(29,158,117,0.18)', animation: 'db-pulse 1.6s ease-in-out infinite', display: 'inline-block' }} />
              Checked in <span style={{ color: 'var(--smc-tx)', opacity: 0.9 }}>· {fmtTime(myAttToday.checkedInAt)}</span>
            </div>
          ) : (
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: 8, padding: '6px 11px 6px 9px',
              border: '0.5px solid var(--smc-bd)', background: 'transparent',
              color: 'var(--smc-tx3)', borderRadius: 999,
              fontFamily: MONO, fontSize: 11, letterSpacing: '0.08em',
            }}>
              <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--smc-tx3)', display: 'inline-block' }} />
              {timeStr}
            </div>
          )}
          <ThemePicker themeId={themeId} onThemeChange={onThemeChange} />
          <Link to="/mission_planning" className="db-btn" style={{
            display: 'inline-flex', alignItems: 'center', gap: 7, padding: '7px 13px', borderRadius: 5,
            border: '0.5px solid var(--smc-ac-border,rgba(14,156,192,0.28))', background: 'var(--smc-ac-bg,rgba(14,156,192,0.10))',
            color: 'var(--smc-ac,#0e9cc0)', fontSize: 12, fontWeight: 600, textDecoration: 'none',
            transition: 'background .12s, border-color .12s',
          }}>
            <span style={{ fontFamily: MONO, fontWeight: 400 }}>+</span> New Report
          </Link>
        </div>
      </div>

      {/* ── Content ─────────────────────────────────────────────────────── */}
      <div className="smc-dashboard-content smc-page-container" style={{ padding: '22px 28px 60px', maxWidth: 1320 }}>

        {/* Overview stats — single card, tap each metric to open */}
        <div className="smc-dashboard-stats" style={{ marginBottom: 18 }}>
          <UnifiedStatsCard
            title="Company Overview"
            meta="TAP A METRIC TO OPEN"
            tiles={statTiles}
          />
        </div>

        {/* Daily Tasks — before Portal Activity */}
        <div className="smc-dashboard-daily-tasks" style={{ marginBottom: 18 }}>
          <DailyTasksPage
            user={{ uid: userId, name: userName }}
            embedded
            onOpenFull={() => goTab('mytasks')}
          />
        </div>

        {/* Main sections — Portal Activity, then Team Attendance (stacked when narrow) */}
        <div className="smc-dashboard-main">

          {/* Portal Activity */}
          <div className="smc-dashboard-portal-activity">
          <CardShell
            title="Portal Activity"
            meta={isAdminView
              ? `${totalReports} REPORTS · ${activePortals.length} PORTALS`
              : myPortalId
                ? `MY DEPARTMENT${sharedPortalCards.length ? ` + ${sharedPortalCards.length} SHARED` : ''}`
                : sharedPortalCards.length ? `${sharedPortalCards.length} SHARED WITH ME` : 'NO PORTALS ASSIGNED'}
            rightEl={isAdminView ? (
              <div data-portal-drop style={{ position: 'relative' }}>
                <button
                  onClick={e => { e.stopPropagation(); setShowPortalDrop(v => !v); }}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 5,
                    padding: '5px 10px', borderRadius: 4,
                    border: '0.5px solid var(--smc-ac-border,rgba(14,156,192,0.28))',
                    background: 'var(--smc-ac-bg,rgba(14,156,192,0.10))',
                    color: 'var(--smc-ac,#0e9cc0)', fontSize: 11, fontWeight: 600,
                    fontFamily: MONO, letterSpacing: '0.08em', cursor: 'pointer',
                  }}
                >
                  + ADD
                </button>
                {showPortalDrop && (
                  <NewPortalDropdown
                    customDepts={customDepts}
                    onCreateDept={() => { setDeptManagerMode('new'); setShowDeptManager(true); }}
                    onClose={() => setShowPortalDrop(false)}
                  />
                )}
              </div>
            ) : null}
          >
            {/* Own dept portals */}
            {visiblePortals.map(({ card, meta }, i) => {
              if (!meta) return null;
              const count = portalCounts[meta.id] || 0;
              const isLast = i === visiblePortals.length - 1 && sharedPortalCards.length === 0;
              return (
                <Link key={meta.id} to={meta.path} className="smc-dashboard-portal-row" style={{
                  display: 'grid', gridTemplateColumns: '3px 1fr auto',
                  alignItems: 'center', gap: 14, padding: '12px 16px',
                  borderBottom: !isLast ? HL : 'none',
                  textDecoration: 'none', transition: 'background .12s',
                }}
                onMouseEnter={e => e.currentTarget.style.background = 'var(--smc-hov,rgba(255,255,255,0.03))'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                >
                  <div style={{ width: 3, height: 28, borderRadius: 2, background: meta.color }} />
                  <div>
                    <div style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--smc-tx)' }}>{meta.label}</div>
                    <div style={{ fontFamily: MONO, fontSize: 10, letterSpacing: '0.14em', color: 'var(--smc-tx3)', marginTop: 3 }}>
                      {count} REPORT{count !== 1 ? 'S' : ''} · {((card.desc || meta.desc || '').split(',')[0] || '').toUpperCase()}
                    </div>
                  </div>
                  <Badge color={meta.color} bg={meta.colorBg} bd={meta.colorBd}>ACTIVE</Badge>
                </Link>
              );
            })}

            {/* Shared portals — click to show only the shared reports */}
            {sharedPortalCards.map((p, i) => {
              const meta = lookupPortalMeta(p.id, customDepts);
              if (!meta) return null;
              const count = sharedReports.filter(r => r.portalId === p.id).length;
              const isLast = i === sharedPortalCards.length - 1;
              return (
                <div key={p.id} className="smc-dashboard-portal-row"
                  onClick={() => setSharedPortalModal(p.id)}
                  style={{
                    display: 'grid', gridTemplateColumns: '3px 1fr auto',
                    alignItems: 'center', gap: 14, padding: '12px 16px',
                    borderBottom: !isLast ? HL : 'none',
                    cursor: 'pointer', transition: 'background .12s',
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = 'var(--smc-hov,rgba(255,255,255,0.03))'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                >
                  <div style={{ width: 3, height: 28, borderRadius: 2, background: meta.color, opacity: 0.6 }} />
                  <div>
                    <div style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--smc-tx)' }}>{meta.label}</div>
                    <div style={{ fontFamily: MONO, fontSize: 10, letterSpacing: '0.14em', color: 'var(--smc-tx3)', marginTop: 3 }}>
                      {count} SHARED REPORT{count !== 1 ? 'S' : ''} · CLICK TO VIEW
                    </div>
                  </div>
                  <Badge color={meta.color} bg={meta.colorBg} bd={meta.colorBd}>SHARED</Badge>
                </div>
              );
            })}

            {/* Empty state for non-admin with no portals */}
            {!isAdminView && visiblePortals.length === 0 && sharedPortalCards.length === 0 && (
              <div style={{ padding: '32px 16px', textAlign: 'center' }}>
                <div style={{ fontSize: 28, opacity: 0.2, marginBottom: 8 }}>🏢</div>
                <div style={{ fontFamily: MONO, fontSize: 10, color: 'var(--smc-tx3)', letterSpacing: '0.1em' }}>
                  NO DEPARTMENT ASSIGNED
                </div>
                <div style={{ fontFamily: MONO, fontSize: 9, color: 'var(--smc-tx3)', marginTop: 4, opacity: 0.6 }}>
                  Contact admin to get a role assigned
                </div>
              </div>
            )}
          </CardShell>
          </div>

          {/* Team Attendance — below Portal Activity when stacked */}
          <div className="smc-dashboard-team-attendance">
          {isAdminView ? (
            <CardShell
              title="Team Attendance Today"
              meta={`${checkedInCount} / ${team.length} CHECKED IN`}
            >
              {team.length === 0 && (
                <div style={{ padding: '20px 16px', fontFamily: MONO, fontSize: 10, color: 'var(--smc-tx3)' }}>Loading…</div>
              )}
              {team.map((member, i) => {
                const att = attendance[member.uid];
                const checkedIn  = att?.isOpen === true;
                const checkedOut = att?.checkedInAt && att?.isOpen === false;
                const checkTime  = att?.checkedInAt ? fmtTime(att.checkedInAt) : null;
                return (
                  <div
                    key={member.uid}
                    className="smc-dashboard-att-row"
                    onClick={() => setSelectedMember({ member, colorIdx: i })}
                    style={{
                      display: 'grid', gridTemplateColumns: '28px 1fr auto auto',
                      alignItems: 'center', gap: 12, padding: '10px 16px',
                      borderBottom: i < team.length - 1 ? HL : 'none',
                      cursor: 'pointer', transition: 'background .12s',
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--smc-hov,rgba(255,255,255,0.03))'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  >
                    <Avatar name={member.name || member.email} colorIdx={i} size={24} />
                    <div>
                      <div style={{ fontSize: 13, color: 'var(--smc-tx)', fontWeight: 500 }}>
                        {member.name || member.email?.split('@')[0] || '—'}
                      </div>
                      <div style={{ fontFamily: MONO, fontSize: 9.5, letterSpacing: '0.16em', color: 'var(--smc-tx3)', marginTop: 2 }}>
                        {(member.role || 'MEMBER').toUpperCase()}
                      </div>
                    </div>
                    <div style={{ fontFamily: MONO, fontSize: 10.5, color: checkedIn ? 'var(--smc-tx2)' : 'var(--smc-tx3)', letterSpacing: '0.05em' }}>
                      {checkTime || '—'}
                    </div>
                    {checkedIn  ? <Badge color="var(--smc-gn,#1D9E75)" bg="var(--smc-gn-bg)" bd="rgba(29,158,117,0.28)">ACTIVE</Badge>
                    : checkedOut ? <Badge color="var(--smc-tx3)" bg="var(--smc-el)" bd="var(--smc-bd)">OUT</Badge>
                    :              <Badge color="var(--smc-tx3)" bg="transparent" bd="var(--smc-bd)">NOT IN</Badge>}
                  </div>
                );
              })}
            </CardShell>
          ) : (
            <CardShell title="My Attendance Today" meta={checkedInNow ? 'CHECKED IN' : 'NOT CHECKED IN'}>
              <div style={{ padding: '20px 16px', display: 'flex', flexDirection: 'column', gap: 12 }}>
                <div className="smc-dashboard-mini-stats" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
                  {[
                    ['CHECK IN',  myAttToday?.checkedInAt  ? fmtTime(myAttToday.checkedInAt) : '—'],
                    ['CHECK OUT', myAttToday?.checkoutTime ? fmtTime(myAttToday.checkoutTime) : '—'],
                    ['DURATION',  myAttToday?.checkedInAt && myAttToday?.checkoutTime
                      ? fmtDuration(myAttToday.checkedInAt, myAttToday.checkoutTime)
                      : checkedInNow ? 'LIVE' : '—'],
                  ].map(([k, v]) => (
                    <div key={k} style={{ background: 'var(--smc-el,#111d36)', border: HL, borderRadius: 5, padding: '10px 12px' }}>
                      <div style={{ fontFamily: MONO, fontSize: 8, letterSpacing: '0.2em', color: 'var(--smc-tx3)', marginBottom: 6 }}>{k}</div>
                      <div style={{ fontFamily: MONO, fontSize: 14, fontWeight: 700, lineHeight: 1, color: 'var(--smc-tx)' }}>{v}</div>
                    </div>
                  ))}
                </div>
                {checkedInNow
                  ? <Badge color="var(--smc-gn,#1D9E75)" bg="var(--smc-gn-bg)" bd="rgba(29,158,117,0.28)">● ACTIVE NOW</Badge>
                  : myAttToday
                  ? <Badge color="var(--smc-tx3)" bg="var(--smc-el)" bd="var(--smc-bd)">CHECKED OUT</Badge>
                  : <Badge color="var(--smc-tx3)" bg="transparent" bd="var(--smc-bd)">NOT CHECKED IN TODAY</Badge>
                }
              </div>
            </CardShell>
          )}
          </div>
        </div>

        {/* Recent Reports */}
        <div className="smc-dashboard-reports">
        <CardShell
          title={isAdminView ? 'Recent Reports' : 'My Reports'}
          meta={isAdminView ? `${recentReports.length} LATEST` : `${roleFilteredReports.length} REPORT${roleFilteredReports.length !== 1 ? 'S' : ''}`}
          rightEl={
            <div style={{ display: 'flex', gap: 6 }}>
              {['ALL', 'APPROVED', 'MINE'].map(chip => (
                <button key={chip} className="db-chip" onClick={() => setReportChip(chip)} style={{
                  fontFamily: MONO, fontSize: 9.5, letterSpacing: '0.16em',
                  padding: '4px 9px', borderRadius: 999, cursor: 'pointer', transition: 'all .12s',
                  border: `0.5px solid ${reportChip === chip ? 'var(--smc-ac-border)' : 'var(--smc-bd)'}`,
                  color:  reportChip === chip ? 'var(--smc-ac)' : 'var(--smc-tx3)',
                  background: reportChip === chip ? 'var(--smc-ac-bg)' : 'transparent',
                }}>{chip}</button>
              ))}
            </div>
          }
        >
          {filteredReports.length === 0 && (
            <div style={{ padding: '24px 16px', textAlign: 'center' }}>
              <div style={{ fontSize: 28, opacity: 0.25, marginBottom: 10 }}>📄</div>
              <div style={{ fontFamily: MONO, fontSize: 10, color: 'var(--smc-tx3)' }}>
                {recentReports.length === 0 ? 'No reports yet across portals.' : 'No reports match this filter.'}
              </div>
            </div>
          )}
          {filteredReports.map((r, i) => {
            const portal = PORTAL_CARDS.find(p => p.id === r.portalId);
            const meta   = lookupPortalMeta(r.portalId, customDepts) || {};
            const author = r.uploadedBy || 'Unknown';
            const reportPath = portal?.path || (customDepts.find(d => d.id === r.portalId) ? `/dept/${r.portalId}` : '/');
            return (
              <div key={r.id} className="smc-dashboard-report-row" style={{
                display: 'grid', gridTemplateColumns: '3px 1fr 160px 110px 60px',
                alignItems: 'center', gap: 16, padding: '12px 16px',
                borderBottom: i < filteredReports.length - 1 ? HL : 'none',
              }}>
                <div style={{ width: 3, height: 28, borderRadius: 2, background: meta.color || 'var(--smc-ac)' }} />
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--smc-tx)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {r.title || r.text?.slice(0, 60) || 'Untitled report'}
                  </div>
                  <div style={{ fontFamily: MONO, fontSize: 10, letterSpacing: '0.14em', color: 'var(--smc-tx3)', marginTop: 3 }}>
                    {fmtDate(r.uploadedAt)} · {fmtRelative(r.uploadedAt)}
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, overflow: 'hidden' }}>
                  <Avatar name={author} colorIdx={team.findIndex(m => m.uid === r.uploadedByUid) % AVATAR_COLORS.length || 0} size={22} />
                  <span style={{ fontSize: 12, color: 'var(--smc-tx2)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {author.split(' ')[0]}
                  </span>
                </div>
                <Badge color="var(--smc-tx2)" bg="var(--smc-el)" bd="var(--smc-bd)">
                  {(meta.label || portal?.label || r.portalId || 'PORTAL').slice(0, 10).toUpperCase()}
                </Badge>
                <Link to={reportPath} className="db-read-link" style={{
                  fontFamily: MONO, fontSize: 10, letterSpacing: '0.14em',
                  color: 'var(--smc-ac)', justifySelf: 'end', textDecoration: 'none', transition: 'color .12s',
                }}>READ →</Link>
              </div>
            );
          })}
        </CardShell>
        </div>
      </div>

      {/* Member detail modal */}
      {selectedMember && (
        <MemberDetailModal
          member={selectedMember.member}
          todayAtt={attendance[selectedMember.member.uid]}
          colorIdx={selectedMember.colorIdx}
          customDepts={customDepts}
          onClose={() => setSelectedMember(null)}
        />
      )}

      {/* Shared Portal modal — shows only reports shared with this user from that portal */}
      {sharedPortalModal && (
        <div
          onClick={e => e.target === e.currentTarget && setSharedPortalModal(null)}
          style={{
            position: 'fixed', inset: 0, zIndex: 9000,
            background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(4px)',
            display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-end',
          }}
        >
          <div style={{
            width: 480, maxWidth: '100vw', height: '100vh', overflowY: 'auto',
            background: 'var(--smc-panel,#0d1526)', borderLeft: HL,
            animation: 'db-slide-in 0.18s ease',
          }}>
            {(() => {
              const p    = PORTAL_CARDS.find(x => x.id === sharedPortalModal);
              const meta = lookupPortalMeta(sharedPortalModal, customDepts) || {};
              const reports = sharedReports.filter(r => r.portalId === sharedPortalModal);
              return (
                <>
                  <div style={{ padding: '20px 20px 16px', borderBottom: HL, display: 'flex', alignItems: 'center', gap: 14 }}>
                    <div>
                      <div style={{ fontFamily: MONO, fontSize: 9, letterSpacing: '0.22em', color: meta.color || 'var(--smc-ac)', marginBottom: 4 }}>
                        SHARED WITH YOU · {meta.label || sharedPortalModal.toUpperCase()}
                      </div>
                      <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--smc-tx)' }}>
                        {reports.length} Shared Report{reports.length !== 1 ? 's' : ''}
                      </div>
                    </div>
                    <button
                      onClick={() => setSharedPortalModal(null)}
                      style={{ marginLeft: 'auto', background: 'transparent', border: '0.5px solid var(--smc-bd)', color: 'var(--smc-tx3)', borderRadius: 4, width: 26, height: 26, cursor: 'pointer', fontSize: 14 }}
                    >✕</button>
                  </div>
                  <div style={{ padding: '12px 0' }}>
                    {reports.length === 0 && (
                      <div style={{ padding: '40px 20px', textAlign: 'center', fontFamily: MONO, fontSize: 10, color: 'var(--smc-tx3)' }}>
                        No shared reports found.
                      </div>
                    )}
                    {reports.map((r, i) => (
                      <Link key={r.id} to={p?.path || '/'} style={{
                        display: 'flex', alignItems: 'flex-start', gap: 12,
                        padding: '12px 20px', borderBottom: i < reports.length - 1 ? HL : 'none',
                        textDecoration: 'none', transition: 'background .12s',
                      }}
                        onMouseEnter={e => e.currentTarget.style.background = 'var(--smc-hov,rgba(255,255,255,0.03))'}
                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                      >
                        <div style={{ width: 3, alignSelf: 'stretch', borderRadius: 2, background: meta.color || 'var(--smc-ac)', flexShrink: 0 }} />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--smc-tx)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {r.title || r.text?.slice(0, 80) || 'Untitled'}
                          </div>
                          <div style={{ fontFamily: MONO, fontSize: 9, letterSpacing: '0.12em', color: 'var(--smc-tx3)', marginTop: 4 }}>
                            {fmtDate(r.uploadedAt)} · by {r.uploadedBy || '—'}
                          </div>
                        </div>
                        <span style={{ fontFamily: MONO, fontSize: 10, color: 'var(--smc-ac)', flexShrink: 0 }}>→</span>
                      </Link>
                    ))}
                  </div>
                </>
              );
            })()}
          </div>
        </div>
      )}

      {showDeptManager && isAdminView && (
        <DepartmentManager
          isAdmin
          userId={userId}
          userName={userName}
          initialEditing={deptManagerMode}
          onClose={() => { setShowDeptManager(false); setDeptManagerMode(null); }}
        />
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
//  ADMIN HOME — redesign (v2, Lovable "mission control")
//  Same data as the classic home plus read-only pending-approval / task-status
//  listeners. Approve/reject still happens in Requests / Attendance → Approvals.
// ══════════════════════════════════════════════════════════════════════════════
const STATE_STYLE = {
  active:      { label: 'ACTIVE',      color: 'var(--smc-gn)' },
  checked_out: { label: 'CHECKED OUT', color: 'var(--smc-tx2)' },
  leave:       { label: 'ON LEAVE',    color: 'var(--smc-ac)' },
  not_in:      { label: 'NOT IN',      color: 'var(--smc-rd)' },
};

export function V2Badge({ color = 'var(--smc-tx2)', children }) {
  return (
    <span className="smc2-badge" style={{
      color, borderColor: `color-mix(in srgb, ${color} 35%, transparent)`,
      background: `color-mix(in srgb, ${color} 10%, transparent)`,
    }}>{children}</span>
  );
}

export function V2RecordId({ children }) {
  return (
    <span className="smc2-record-id" style={{
      border: '1px solid var(--smc-bd)', background: 'var(--smc-deep)', borderRadius: 4, padding: '1px 6px',
    }}>{children}</span>
  );
}

export function V2Panel({ title, id, action, children, flush }) {
  return (
    <section className="smc2-panel" style={{ minWidth: 0 }}>
      {title && (
        <header style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
          padding: '12px 16px', borderBottom: '1px solid var(--smc-bd)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
            <h2 className="smc2-hud" style={{ margin: 0, color: 'var(--smc-tx2)', whiteSpace: 'nowrap',
              overflow: 'hidden', textOverflow: 'ellipsis' }}>{title}</h2>
            {id && <V2RecordId>{id}</V2RecordId>}
          </div>
          {action}
        </header>
      )}
      <div style={{ padding: flush ? 0 : 16 }}>{children}</div>
    </section>
  );
}

export function V2Stat({ label, value, sub, color, onClick }) {
  return (
    <button type="button" onClick={onClick} className="smc2-panel smc2-v2stat"
      style={{ '--c': color, textAlign: 'left', cursor: onClick ? 'pointer' : 'default' }}>
      <span className="smc2-hud" style={{ color: 'var(--smc-tx3)' }}>{label}</span>
      <span className="smc2-v2stat-val">{value}</span>
      {sub && <span style={{ fontSize: 12, color: 'var(--smc-tx2)' }}>{sub}</span>}
    </button>
  );
}

export function V2Avatar({ name, color = 'var(--smc-ac)' }) {
  return (
    <span style={{
      display: 'grid', placeItems: 'center', width: 32, height: 32, flexShrink: 0, borderRadius: '50%',
      border: `1px solid ${color}`, background: 'var(--smc-el)', color,
      fontFamily: MONO, fontSize: 11,
    }}>{getInitials(name)}</span>
  );
}

export function AdminHomeV2({
  userId, userName, clock, team, attendance, customDepts, portalCounts, totalReports,
  activePortals, recentReports, goTab, onOpenMember, addPortalControl,
  preview = false, // dev layout preview: no Firestore listeners
}) {
  const navigate = useNavigate();
  const [pendingAccess, setPendingAccess] = useState([]);
  const [pendingLeave,  setPendingLeave]  = useState([]);
  const [leaveToday,    setLeaveToday]    = useState(new Set());
  const [tasks,         setTasks]         = useState([]);
  const [chip,          setChip]          = useState('ALL');
  const today = todayIST();

  // Read-only listeners for the approval queue, today's leave and task status
  useEffect(() => {
    if (preview) return undefined;
    const db = DB();
    const subs = [
      onSnapshot(query(collection(db, 'access_requests'), where('status', '==', 'pending')),
        s => setPendingAccess(s.docs.map(d => ({ id: d.id, ...d.data() }))), () => {}),
      onSnapshot(query(collection(db, 'leave_requests'), where('status', '==', 'pending')),
        s => setPendingLeave(s.docs.map(d => ({ id: d.id, ...d.data() }))), () => {}),
      onSnapshot(query(collection(db, 'leave_requests'), where('status', '==', 'approved')),
        s => setLeaveToday(new Set(s.docs.map(d => d.data())
          .filter(r => r.fromDate && r.fromDate <= today && (r.toDate || r.fromDate) >= today)
          .map(r => r.employeeUid))), () => {}),
      onSnapshot(collection(db, 'tasks'),
        s => setTasks(s.docs.map(d => d.data())), () => {}),
    ];
    return () => subs.forEach(u => u());
  }, [today, preview]);

  const stateOf = m => {
    const att = attendance[m.uid];
    if (att?.isOpen === true) return 'active';
    if (att?.checkedInAt) return 'checked_out';
    if (leaveToday.has(m.uid)) return 'leave';
    return 'not_in';
  };
  const crew = team.map((m, i) => ({ m, i, state: stateOf(m) }));
  const count = st => crew.filter(c => c.state === st).length;

  const taskBuckets = [
    { id: 'open',        label: 'OPEN',        color: 'var(--smc-am)', n: tasks.filter(t => t.status === 'pending' && !(t.dueDate && t.dueDate < today)).length },
    { id: 'in_progress', label: 'IN PROGRESS', color: 'var(--smc-ac)', n: tasks.filter(t => t.status === 'in_progress' && !(t.dueDate && t.dueDate < today)).length },
    { id: 'done',        label: 'DONE',        color: 'var(--smc-gn)', n: tasks.filter(t => t.status === 'completed').length },
    { id: 'overdue',     label: 'OVERDUE',     color: 'var(--smc-rd)', n: tasks.filter(t => t.status !== 'completed' && t.dueDate && t.dueDate < today).length },
  ];

  const deptOf = m => lookupPortalMeta(m.role, customDepts) || lookupPortalMeta((m.portals || [])[0], customDepts);
  const openCount = pendingAccess.length + pendingLeave.length;
  const eyebrow = clock.toLocaleDateString('en-IN', {
    timeZone: 'Asia/Kolkata', weekday: 'short', day: '2-digit', month: 'short', year: 'numeric',
  }).toUpperCase().replace(/,/g, '');
  const timeStr = clock.toLocaleTimeString('en-IN', {
    timeZone: 'Asia/Kolkata', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
  });

  const reports = recentReports.filter(r => {
    if (chip === 'MINE') return r.uploadedByUid === userId || r.uploadedBy === userName;
    if (chip === 'APPROVED') return r.adminLiked === true || r.approved === true;
    return true;
  });

  return (
    <div className="smc2-home">
      <div className="smc2-page-title">
        <div>
          <div className="smc2-hud" style={{ color: 'var(--smc-ac)' }}>{eyebrow} · COMPANY OVERVIEW</div>
          <h1>{getGreeting()}, {userName?.split(' ')[0] || 'Commander'}</h1>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span className="smc2-chip" style={{ fontSize: 11 }}>{timeStr} IST</span>
          <button type="button" className="smc2-btn" onClick={() => goTab('reports')}>REPORT REGISTER</button>
          <Link to="/mission_planning" className="smc2-btn smc2-btn--primary" style={{ textDecoration: 'none' }}>+ NEW REPORT</Link>
        </div>
      </div>

      {/* Attendance stats */}
      <div className="smc2-stats">
        <V2Stat label="ACTIVE NOW"  value={count('active')} sub={`of ${team.length} crew`} color="var(--smc-gn)"
          onClick={() => goTab('attendance')} />
        <V2Stat label="CHECKED OUT" value={count('checked_out')} color="var(--smc-tx3)" onClick={() => goTab('attendance')} />
        <V2Stat label="NOT IN"      value={count('not_in')} color="var(--smc-rd)" onClick={() => goTab('attendance')} />
        <V2Stat label="ON LEAVE"    value={count('leave')} color="var(--smc-ac)" onClick={() => goTab('attendance')} />
      </div>

      {/* Company overview — the classic home's metrics, one tap each */}
      <div className="smc2-overview">
        {[
          ['MISSION PHASE', 'Phase 1', 'IAD · ELECTRIC THRUSTER', () => goTab('missions')],
          ['TEAM MEMBERS', team.length || '—', 'ACTIVE ACCOUNTS', () => goTab('team', { teamSubTab: 'manage' })],
          ['TOTAL REPORTS', totalReports || '—', 'ACROSS ALL PORTALS', () => goTab('reports')],
          ['PORTALS ACTIVE', activePortals.length, `${activePortals.length} LIVE`, () => goTab('team')],
        ].map(([l, v, f, fn]) => (
          <button key={l} type="button" className="smc2-overview-item" onClick={fn}>
            <span className="smc2-hud" style={{ color: 'var(--smc-tx3)', fontSize: 9 }}>{l}</span>
            <span style={{ fontFamily: MONO, fontSize: 18, color: 'var(--smc-tx)' }}>{v}</span>
            <span className="smc2-hud" style={{ color: 'var(--smc-tx3)', fontSize: 8, letterSpacing: '0.12em' }}>{f}</span>
          </button>
        ))}
      </div>

      <div className="smc2-home-grid">
        {/* Team attendance today */}
        <V2Panel title="TEAM ATTENDANCE TODAY" id={`ATT-${today.replace(/-/g, '')}`}
          action={<V2Badge color="var(--smc-gn)">{count('active')} / {team.length} IN</V2Badge>} flush>
          {team.length === 0 && <div style={{ padding: 16, color: 'var(--smc-tx3)', fontFamily: MONO, fontSize: 10 }}>LOADING…</div>}
          <ul className="smc2-list">
            {crew.map(({ m, i, state }) => {
              const dept = deptOf(m);
              const st = STATE_STYLE[state];
              const t = fmtTime(attendance[m.uid]?.checkedInAt);
              return (
                <li key={m.uid}>
                  <button type="button" className="smc2-list-row" onClick={() => onOpenMember(m, i)}>
                    <V2Avatar name={m.name || m.email} color={dept?.color || 'var(--smc-ac)'} />
                    <span style={{ minWidth: 0, flex: 1 }}>
                      <span style={{ display: 'block', fontSize: 14, fontWeight: 500, color: 'var(--smc-tx)',
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {m.name || m.email?.split('@')[0] || '—'}
                      </span>
                      <span className="smc2-record-id" style={{ display: 'block', marginTop: 2 }}>
                        {(m.role || 'member').toUpperCase()}{dept?.label ? ` · ${dept.label.toUpperCase()}` : ''}
                      </span>
                    </span>
                    {t && <span className="smc2-hide-xs" style={{ fontFamily: MONO, fontSize: 12, color: 'var(--smc-tx2)' }}>{t}</span>}
                    <V2Badge color={st.color}>{st.label}</V2Badge>
                  </button>
                </li>
              );
            })}
          </ul>
        </V2Panel>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 20, minWidth: 0 }}>
          {/* Pending approvals */}
          <V2Panel title="PENDING APPROVALS" action={<V2Badge color="var(--smc-am)">{openCount} OPEN</V2Badge>}>
            {openCount === 0 && (
              <div style={{ color: 'var(--smc-tx3)', fontSize: 13, textAlign: 'center', padding: '12px 0' }}>All clear — nothing waiting.</div>
            )}
            <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 10 }}>
              {pendingAccess.slice(0, 4).map(r => (
                <li key={r.id} className="smc2-approval">
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                    <V2RecordId>REQ-{r.id.slice(0, 6).toUpperCase()}</V2RecordId>
                    <V2Badge color="var(--smc-pu)">ACCESS</V2Badge>
                  </div>
                  <div style={{ marginTop: 8, fontSize: 14, fontWeight: 500, color: 'var(--smc-tx)' }}>
                    {r.name || r.email || 'Applicant'}
                    <span style={{ fontWeight: 400, color: 'var(--smc-tx2)' }}> · {r.requestedRole || r.roleLabel || r.team || 'role'}</span>
                  </div>
                  <button type="button" className="smc2-btn" style={{ marginTop: 8, height: 26 }}
                    onClick={() => goTab('requests')}>REVIEW →</button>
                </li>
              ))}
              {pendingLeave.slice(0, 4).map(r => (
                <li key={r.id} className="smc2-approval">
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                    <V2RecordId>LVE-{r.id.slice(0, 6).toUpperCase()}</V2RecordId>
                    <V2Badge color="var(--smc-ac)">LEAVE · {r.totalDays || '?'}D</V2Badge>
                  </div>
                  <div style={{ marginTop: 8, fontSize: 14, fontWeight: 500, color: 'var(--smc-tx)' }}>
                    {r.employeeName || 'Member'}
                    <span style={{ fontWeight: 400, color: 'var(--smc-tx2)' }}>
                      {' '}· {r.leaveType || 'leave'}, {r.fromDate === r.toDate || !r.toDate ? r.fromDate : `${r.fromDate} → ${r.toDate}`}
                    </span>
                  </div>
                  <button type="button" className="smc2-btn" style={{ marginTop: 8, height: 26 }}
                    onClick={() => navigate('/app/attendance?view=approvals')}>REVIEW →</button>
                </li>
              ))}
            </ul>
            {openCount > 8 && (
              <div className="smc2-hud" style={{ color: 'var(--smc-tx3)', fontSize: 9, marginTop: 10 }}>
                +{openCount - 8} MORE IN REQUESTS / ATTENDANCE
              </div>
            )}
          </V2Panel>

          {/* Task status */}
          <V2Panel title="TASK STATUS" id="ALL DEPTS"
            action={<button type="button" className="smc2-btn" style={{ height: 26 }} onClick={() => goTab('mytasks')}>OPEN →</button>}>
            <div className="smc2-taskgrid">
              {taskBuckets.map(b => (
                <div key={b.id} style={{ background: 'var(--smc-el)', borderRadius: 6, padding: '12px 4px', textAlign: 'center' }}>
                  <div style={{ fontFamily: MONO, fontSize: 24, color: 'var(--smc-tx)' }}>{b.n}</div>
                  <div style={{ marginTop: 6 }}><V2Badge color={b.color}>{b.label}</V2Badge></div>
                </div>
              ))}
            </div>
          </V2Panel>
        </div>
      </div>

      {/* Daily tasks (kept from classic home) */}
      {!preview && (
        <div style={{ marginBottom: 20 }}>
          <DailyTasksPage user={{ uid: userId, name: userName }} embedded onOpenFull={() => goTab('mytasks')} />
        </div>
      )}

      {/* Portal activity */}
      <V2Panel title="PORTAL ACTIVITY" id={`${totalReports} REPORTS`} action={addPortalControl}>
        <div className="smc2-portalgrid">
          {activePortals.map(({ card, meta }) => {
            if (!meta) return null;
            const n = portalCounts[meta.id] || 0;
            const code = portalCode(meta.id);
            return (
              <Link key={meta.id} to={meta.path} className="smc2-portalcard" style={{ '--c': meta.color }}>
                <span style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                  <span className="smc2-hud" style={{ color: meta.color }}>{code}</span>
                  <V2RecordId>DEP-{code}</V2RecordId>
                </span>
                <span style={{ display: 'block', marginTop: 8, fontSize: 14, fontWeight: 500, color: 'var(--smc-tx)',
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{meta.label}</span>
                <span style={{ display: 'block', marginTop: 12, height: 4, borderRadius: 4, background: 'var(--smc-deep)' }}>
                  <span style={{ display: 'block', height: 4, borderRadius: 4, background: meta.color,
                    width: `${totalReports ? Math.max(4, Math.round((n / Math.max(...Object.values(portalCounts), 1)) * 100)) : 0}%` }} />
                </span>
                <span className="smc2-hud" style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8,
                  fontSize: 9, letterSpacing: '0.12em', color: 'var(--smc-tx3)' }}>
                  <span>{n} REPORT{n !== 1 ? 'S' : ''}</span><span>→</span>
                </span>
              </Link>
            );
          })}
        </div>
      </V2Panel>

      {/* Recent reports */}
      <div style={{ marginTop: 20 }}>
        <V2Panel title="RECENT REPORTS" id={`${recentReports.length} LATEST`} flush
          action={
            <div style={{ display: 'flex', gap: 6 }}>
              {['ALL', 'APPROVED', 'MINE'].map(c => (
                <button key={c} type="button" onClick={() => setChip(c)} className="smc2-btn"
                  style={{ height: 24, padding: '0 8px', fontSize: 9,
                    ...(chip === c ? { color: 'var(--smc-ac)', borderColor: 'var(--smc-ac)', background: 'var(--smc-ac-bg)' } : {}) }}>
                  {c}
                </button>
              ))}
            </div>
          }>
          {reports.length === 0 && (
            <div style={{ padding: '28px 16px', textAlign: 'center', color: 'var(--smc-tx3)', fontSize: 13 }}>
              {recentReports.length === 0 ? 'No reports yet across portals.' : 'No reports match this filter.'}
            </div>
          )}
          <ul className="smc2-list">
            {reports.map(r => {
              const meta = lookupPortalMeta(r.portalId, customDepts) || {};
              const portal = PORTAL_CARDS.find(p => p.id === r.portalId);
              const path = portal?.path || (customDepts.find(d => d.id === r.portalId) ? `/dept/${r.portalId}` : '/');
              return (
                <li key={`${r.portalId}:${r.id}`}>
                  <div className="smc2-list-row" style={{ cursor: 'default' }}>
                    <span style={{ width: 3, alignSelf: 'stretch', borderRadius: 2, background: meta.color || 'var(--smc-ac)', flexShrink: 0 }} />
                    <span style={{ minWidth: 0, flex: 1 }}>
                      <span style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                        <V2RecordId>{reportId(r, r.portalId)}</V2RecordId>
                        <V2Badge color={meta.color || 'var(--smc-tx2)'}>{meta.label || r.portalId || 'PORTAL'}</V2Badge>
                      </span>
                      <span style={{ display: 'block', marginTop: 4, fontSize: 14, fontWeight: 500, color: 'var(--smc-tx)',
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {r.title || r.text?.slice(0, 60) || 'Untitled report'}
                      </span>
                      <span className="smc2-record-id" style={{ display: 'block', marginTop: 2 }}>
                        {r.uploadedBy || 'Unknown'} · {fmtDate(r.uploadedAt)} · {fmtRelative(r.uploadedAt)}
                      </span>
                    </span>
                    <span style={{ display: 'flex', gap: 6, alignItems: 'center', flexShrink: 0 }}>
                      <ReportShareButton post={r} portalId={r.portalId} portalLabel={meta.label} portalColor={meta.color}
                        portalPath={path} currentUser={{ uid: userId, name: userName, role: 'admin' }} />
                      <Link to={`${path}?post=${encodeURIComponent(r.id)}`} className="smc2-btn"
                        style={{ height: 26, textDecoration: 'none' }}>READ →</Link>
                    </span>
                  </div>
                </li>
              );
            })}
          </ul>
        </V2Panel>
      </div>
    </div>
  );
}
